/**
 * Documentos de um usuário para a contabilidade.
 * Principais ficam juntos. Atestados ficam agrupados pelo mês do envio.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Download } from 'lucide-react'
import { userDocumentService } from '../../../services/documentsApi'
import { downloadDocumentFile } from '../../../utils/downloadDocumentFile'
import './documentsShell.css'
import './UserDocumentsDetailPage.css'

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
]

function isAtestado(document) {
  const text = `${document.category_name || ''} ${document.title || ''}`.toLowerCase()
  return text.includes('atestado')
}

function monthKey(value) {
  if (!value) return 'sem-data'
  const raw = String(value).slice(0, 7)
  return /^\d{4}-\d{2}$/.test(raw) ? raw : 'sem-data'
}

function monthLabel(key) {
  if (key === 'sem-data') return 'Sem data de envio'
  const [year, month] = key.split('-')
  const name = MONTHS[Number(month) - 1] || month
  return `${name} ${year}`
}

function formatDate(value) {
  if (!value) return ''
  const [year, month, day] = String(value).slice(0, 10).split('-')
  if (!year || !month || !day) return ''
  return `${day}/${month}/${year}`
}

function DocumentLine({ document }) {
  return (
    <div className="docs-row docs-file-row">
      <div>
        <strong>{document.title || document.category_name || 'Documento'}</strong>
        <span>
          {[document.category_name, document.file_name, formatDate(document.uploaded_at)].filter(Boolean).join(' · ')}
        </span>
      </div>
      {document.file_url ? (
        <button type="button" className="docs-download" onClick={() => downloadDocumentFile(document)}>
          <Download size={16} />
          Baixar
        </button>
      ) : null}
    </div>
  )
}

function UserDocumentsDetailPage() {
  const { userId } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [payload, setPayload] = useState(null)

  useEffect(() => {
    let active = true
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const response = await userDocumentService.getByUserId(userId)
        if (!active) return
        if (response.success) {
          setPayload(response.data)
        } else {
          setError(response.message || 'Não foi possível carregar os documentos.')
        }
      } catch (err) {
        if (!active) return
        setError(err.response?.data?.message || 'Não foi possível carregar os documentos.')
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [userId])

  const grouped = useMemo(() => {
    const documents = payload?.documents || []
    const principais = []
    const atestados = new Map()

    documents.forEach((document) => {
      if (isAtestado(document)) {
        const key = monthKey(document.uploaded_at)
        const list = atestados.get(key) || []
        list.push(document)
        atestados.set(key, list)
        return
      }
      principais.push(document)
    })

    const months = [...atestados.entries()].sort((a, b) => b[0].localeCompare(a[0]))
    return { principais, months }
  }, [payload])

  if (loading) {
    return (
      <div className="docs-page">
        <div className="docs-state">Carregando documentos...</div>
      </div>
    )
  }

  if (error || !payload?.user) {
    return (
      <div className="docs-page">
        <Link to="/erp/documents/users" className="docs-back">
          <ArrowLeft size={16} />
          Voltar
        </Link>
        <div className="docs-state">{error || 'Usuário não encontrado.'}</div>
      </div>
    )
  }

  const { user } = payload

  return (
    <div className="docs-page">
      <header className="docs-header">
        <div>
          <Link to="/erp/documents/users" className="docs-back">
            <ArrowLeft size={16} />
            Documentos dos usuários
          </Link>
          <h1 className="docs-title">
            {user.name} <span className="docs-title-highlight">Documentos</span>
          </h1>
          <p className="docs-subtitle">
            {user.access_group_name || 'Grupo de acesso'} · principais e atestados por mês
          </p>
        </div>
      </header>

      <section className="docs-panel">
        <h2>Documentos principais</h2>
        {grouped.principais.length === 0 ? (
          <p className="docs-empty">Nenhum documento principal enviado.</p>
        ) : grouped.principais.map((document) => (
          <DocumentLine key={document.id} document={document} />
        ))}
      </section>

      <section className="docs-panel">
        <h2>Atestados</h2>
        {grouped.months.length === 0 ? (
          <p className="docs-empty">Nenhum atestado enviado.</p>
        ) : grouped.months.map(([key, documents]) => (
          <div key={key} className="docs-month">
            <h3>{monthLabel(key)}</h3>
            {documents.map((document) => (
              <DocumentLine key={document.id} document={document} />
            ))}
          </div>
        ))}
      </section>
    </div>
  )
}

export default UserDocumentsDetailPage
